import { setConnectedServer } from '@/stores/connectedServer';
import { closeDialog } from '@/stores/dialog';
import { setServerState } from '@/stores/server';
import { encodeConnectionKey } from '@/utils/connectionKeyUtils';
import { invoke } from '@tauri-apps/api/core';
import { useEffect, useState } from 'react';

type ServerInfoResponse = {
  port: number;
  ip: string[];
};

export function ServerSettingsDialog() {
  const [serverInfo, setServerInfo] = useState<ServerInfoResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  const [copiedKey, setCopiedKey] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState<string | null>(null);

  const fetchServerInfo = async () => {
    try {
      setError(null);
      const info = await invoke<ServerInfoResponse>('get_server_info');
      setServerInfo(info);
      setSelectedAddress(info.ip[0] ?? null);
      setServerState(
        info.ip.length > 0 ? { ip: info.ip[0]!, port: info.port } : undefined,
      );
      setConnectedServer(null);
    } catch (err) {
      setServerInfo(null);
      setServerState(undefined);
      // Not an error if server is just not running yet
      if (typeof err === 'string' && err.includes('not running')) {
        setError(null);
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void fetchServerInfo();
    }, 0);

    return () => window.clearTimeout(timer);
  }, []);

  const serverInfos =
    serverInfo?.ip.map(ip => ({ ip, port: serverInfo.port })) ?? [];
  const selectedServerInfo =
    serverInfos.find(entry => entry.ip === selectedAddress) ?? serverInfos[0];
  const connectionKey = selectedServerInfo
    ? encodeConnectionKey(selectedServerInfo)
    : '';

  const handleStart = async () => {
    setIsStarting(true);
    try {
      await invoke('start_server');
      await new Promise(resolve => setTimeout(resolve, 500)); // Wait for server to start
      setConnectedServer(null);
      await fetchServerInfo();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsStarting(false);
    }
  };

  const handleStop = async () => {
    setIsStopping(true);
    try {
      await invoke('stop_server');
      setServerInfo(null);
      setSelectedAddress(null);
      setServerState(undefined);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsStopping(false);
    }
  };

  const handleCopyKey = async (key: string) => {
    if (key) {
      await navigator.clipboard.writeText(key);
      setCopiedKey(true);
      setTimeout(() => setCopiedKey(false), 2000);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg">
        <h2 className="mb-4 text-2xl font-bold">Server Settings</h2>

        {error && <p className="mb-4 text-red-600">Error: {error}</p>}

        {!serverInfo ? (
          <div className="space-y-4">
            <p className="text-gray-600">
              Server is currently{' '}
              <span className="font-semibold">not running</span>.
            </p>
            <button
              className="w-full rounded bg-green-600 px-4 py-2 font-semibold text-white hover:bg-green-700 disabled:bg-gray-400"
              onClick={handleStart}
              disabled={isStarting || loading}
            >
              {isStarting ? 'Starting...' : 'Start Server'}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-gray-600">
              Server is currently{' '}
              <span className="font-semibold text-green-600">running</span>.
            </p>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Available Addresses
              </label>
              <div className="mt-2 space-y-2">
                {serverInfos.map(serverInfoEntry => {
                  const isSelected =
                    selectedServerInfo?.ip === serverInfoEntry.ip;
                  return (
                    <button
                      key={`${serverInfoEntry.ip}:${serverInfoEntry.port}`}
                      type="button"
                      onClick={() => setSelectedAddress(serverInfoEntry.ip)}
                      className={`w-full rounded border p-3 text-left ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 bg-gray-50 hover:bg-gray-100'
                      }`}
                    >
                      <p className="font-mono text-sm">
                        http://{serverInfoEntry.ip}:{serverInfoEntry.port}
                      </p>
                      <p className="mt-1 text-xs text-gray-500">
                        {serverInfoEntry.ip}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Selected Address
              </label>
              <p className="mt-1 font-mono text-sm">
                {selectedServerInfo?.ip ?? 'No address selected'}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Port
              </label>
              <p className="mt-1 font-mono">{serverInfo.port}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Connection Key
              </label>
              <div className="mt-1 flex items-center gap-2">
                <code className="flex-1 rounded bg-gray-100 px-3 py-2 font-mono text-sm break-all">
                  {connectionKey}
                </code>
                <button
                  className="rounded bg-gray-200 px-3 py-2 text-sm font-medium hover:bg-gray-300"
                  onClick={() => handleCopyKey(connectionKey)}
                  title="Copy connection key"
                >
                  {copiedKey ? '✓' : '📋'}
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Share this key with others to connect to your server
              </p>
            </div>

            <p className="text-sm text-gray-600">
              Use the server address above to connect from other devices on your
              network.
            </p>

            <button
              className="w-full rounded bg-red-600 px-4 py-2 font-semibold text-white hover:bg-red-700 disabled:bg-gray-400"
              onClick={handleStop}
              disabled={isStopping}
            >
              {isStopping ? 'Stopping...' : 'Stop Server'}
            </button>
          </div>
        )}

        <button
          className="mt-6 w-full rounded bg-blue-600 px-4 py-2 font-semibold text-white hover:bg-blue-700"
          onClick={closeDialog}
        >
          Close
        </button>
      </div>
    </div>
  );
}
